import { useStore } from '~/store';
import { discoverBlobs } from './discover';

/**
 * Attach a debounced subscriber that re-runs CCL blob detection whenever
 * detector inputs change while framing mode is 'pin'.
 * Reflects in-flight status via `pin.detecting`.
 *
 * The store does not use the `subscribeWithSelector` middleware, so we use
 * a plain `subscribe` with manual selector diffing.
 *
 * @returns Unsubscribe function. Also clears any pending debounce timer.
 */
export function attachLiveDetect(): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const unsubscribe = useStore.subscribe((state, prevState) => {
    const cleanedChanged = state.background.cleaned !== prevState.background.cleaned;
    const toleranceChanged = state.pin.detectTolerance !== prevState.pin.detectTolerance;
    const minAreaChanged = state.pin.detectMinArea !== prevState.pin.detectMinArea;
    const framingModeChanged = state.pin.framingMode !== prevState.pin.framingMode;
    if (!cleanedChanged && !toleranceChanged && !minAreaChanged && !framingModeChanged) return;

    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }

    if (state.pin.framingMode !== 'pin' || !state.background.cleaned) {
      if (state.pin.detecting) state.setDetecting(false);
      return;
    }

    if (!state.pin.detecting) state.setDetecting(true);

    timer = setTimeout(() => {
      timer = null;
      const current = useStore.getState();
      const cleaned = current.background.cleaned;
      const { framingMode, detectTolerance, detectMinArea } = current.pin;
      if (!cleaned || framingMode !== 'pin') {
        current.setDetecting(false);
        return;
      }

      const blobs = discoverBlobs(cleaned, {
        alphaThreshold: detectTolerance,
        minArea: detectMinArea,
      });
      current.setBlobs(blobs);
      current.setDetecting(false);
    }, 150);
  });

  return () => {
    unsubscribe();
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    const state = useStore.getState();
    if (state.pin.detecting) state.setDetecting(false);
  };
}
